import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { TopNav } from '../components/top-nav.js';
import {
  type DevTemplateListItem,
  type DevTemplateRenderError,
  type DevTemplateRenderResult,
  devTemplateRender,
  fetchBrands,
  fetchDevTemplates,
} from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { meQuery } from '../lib/queries.js';

// /dev/templates — dev-only HTML/CSS template render sandbox.
//
// POST a template fill, get back a render — no LLM cost, no drop /
// concept bookkeeping. Backs the `/api/__dev/templates/render`
// endpoint which 404s in production.
//
// Use case: verify that an existing template (hook-card-v1,
// steps-list-v1, …) produces the visual look you want for a given
// brand BEFORE asking Mag to author a real creative. Or iterate on
// a fill JSON by hand, see the result, tweak.

// ─── starter presets ────────────────────────────────────────
//
// Pre-built fills modeled on real Gestionalo references. Click a
// preset to load the fill into the editor; tweak + render.
// `format` defaults to feed_1x1 unless the preset declares otherwise.

interface StarterPreset {
  label: string;
  templateKey: string;
  fill: Record<string, unknown>;
  format?: string;
}

const STARTER_PRESETS: StarterPreset[] = [
  {
    label: 'hook-card · big-script kicker (Gestionalo "hola.")',
    templateKey: 'hook-card-v1',
    fill: {
      pillar: 'SERVICIO · APERTURA DE EMPRESA',
      kicker: 'hola.',
      kickerStyle: 'big-script',
      kickerSize: 'huge',
      headline: 'Trámites en una sola página.',
      body: 'Llegamos para que dejes de perder mañanas en filas. Trámites, certificaciones y gestiones: pedís, pagás cuando confirmás, listo.',
      cta: 'empezá ahora',
      handle: 'gestionalo.uy',
      surface: 'ink',
    },
  },
  {
    label: 'hook-card · big-script on primary surface (mint)',
    templateKey: 'hook-card-v1',
    fill: {
      pillar: 'AHORA EN GESTIONALO',
      kicker: 'hola.',
      kickerStyle: 'big-script',
      kickerSize: 'huge',
      headline: 'Trámites en una sola página.',
      body: 'Pedís, pagás cuando confirmás, listo. La burocracia del país pasada por nuestra app.',
      cta: 'bajá la app',
      handle: 'gestionalo.uy',
      surface: 'primary',
    },
  },
  {
    label: 'steps-list · 3 stepper boxes (Gestionalo "Certificado en 24h")',
    templateKey: 'steps-list-v1',
    fill: {
      pillar: 'CÓMO SE HACE',
      headline: 'Certificado en 24h, sin moverte.',
      subhead: 'Tres pasos. El más largo te lleva 3 minutos.',
      steps: [
        {
          heading: 'Entrá',
          body: 'A gestionalo.uy y elegí el servicio que necesitás.',
        },
        {
          heading: 'Cargá',
          body: 'Tus datos básicos y el período a certificar. Listo en 3 minutos.',
        },
        {
          heading: 'Recibí',
          body: 'El PDF firmado por contador habilitado en tu mail.',
        },
      ],
      cta: 'empezá ahora',
      handle: 'gestionalo.uy',
      surface: 'ink',
    },
  },
  {
    label: 'feature-chips-grid · scope chips (icon-chip alternative)',
    templateKey: 'feature-chips-grid-v1',
    fill: {
      pillar: 'SERVICIO · APERTURA DE EMPRESA',
      headline: 'Abrí tu empresa, sin trabarte.',
      subhead:
        'Constitución, inscripciones y libros sociales — gestionado por contador y escribano habilitados, en una sola conversación.',
      chips: [
        { icon: 'Briefcase', label: 'Unipersonal' },
        { icon: 'Buildings', label: 'SAS' },
        { icon: 'Bank', label: 'SRL' },
        { icon: 'CurrencyDollar', label: 'Desde $9.900' },
        { icon: 'CheckCircle', label: 'DGI + BPS' },
      ],
      cta: 'solicitar',
      handle: 'gestionalo.uy',
      surface: 'ink',
    },
  },
  {
    label: 'text-badge-list · dot-bullet badges (Gestionalo "Abrí tu empresa")',
    templateKey: 'text-badge-list-v1',
    fill: {
      pillar: 'SERVICIO · APERTURA DE EMPRESA',
      headline: 'Abrí tu empresa, sin trabarte.',
      badges: ['UNIPERSONAL · SAS · SRL', 'DESDE $ 9.900', 'CONSTITUCIÓN + DGI + BPS'],
      body: 'Constitución, inscripciones y libros sociales — gestionado por contador y escribano habilitados, en una sola conversación.',
      cta: 'solicitar',
      handle: 'gestionalo.uy',
      surface: 'ink',
    },
  },
];

export const Route = createFileRoute('/dev/templates')({
  beforeLoad: async ({ context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
  },
  component: DevTemplatesPage,
});

/** Gate. The sibling dev routes (dev.preview, dev.template-probe) all wrap
 *  their body like this, and this one did not — so while theirs fold away in
 *  a production build, this route's entire sandbox shipped: 11KB against
 *  their 375B and 584B. The bundler replaces import.meta.env.DEV with false
 *  in prod, which makes <TemplatesSandbox /> unreachable and lets the whole
 *  body be dropped; what remains is the notice.
 *
 *  It was never a hole — mountTemplateDevRoutes sits inside a
 *  NODE_ENV !== 'production' check in server.ts, so /api/__dev/templates/*
 *  404s and the sandbox could not act even if reached. It was weight, plus a
 *  live URL that rendered a sandbox with no working endpoints behind it.
 *
 *  Has to be its own component, not an early return inside the sandbox: the
 *  gate must sit ahead of the first hook, and the body opens with
 *  useBrandTitle + useQuery. */
function DevTemplatesPage() {
  if (!import.meta.env.DEV) {
    return (
      <div className="p-6 font-mono text-[13px] text-[var(--ink)]">
        dev-only route. only loaded in development builds.
      </div>
    );
  }
  return <TemplatesSandbox />;
}

function TemplatesSandbox() {
  useBrandTitle('dev', ['templates']);
  const brands = useQuery({
    queryKey: ['brands'] as const,
    queryFn: fetchBrands,
    staleTime: 60_000,
  });
  const templates = useQuery({
    queryKey: ['dev-templates'] as const,
    queryFn: fetchDevTemplates,
    staleTime: 60_000,
  });

  const [selectedBrandId, setSelectedBrandId] = useState<string>('');
  const initialPreset = STARTER_PRESETS[0]!;
  const [templateKey, setTemplateKey] = useState<string>(initialPreset.templateKey);
  const [fillText, setFillText] = useState<string>(JSON.stringify(initialPreset.fill, null, 2));
  const [format, setFormat] = useState<string>(initialPreset.format ?? 'feed_1x1');
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<DevTemplateRenderResult | DevTemplateRenderError | null>(
    null,
  );
  const [parseError, setParseError] = useState<string | null>(null);

  const selectedMeta = useMemo(
    () => (templates.data ?? []).find((t) => t.key === templateKey) ?? null,
    [templates.data, templateKey],
  );

  // Reset to the format dropdown's first allowed value when switching
  // to a template that doesn't support the current format.
  useEffect(() => {
    if (!selectedMeta) return;
    if (!selectedMeta.formats.includes(format)) {
      const fallback = selectedMeta.formats[0];
      if (fallback) setFormat(fallback);
    }
  }, [selectedMeta, format]);

  // s263 — when the user picks a different template via the dropdown,
  // auto-load the first starter preset whose templateKey matches. Skips
  // the auto-load when loadPreset itself just triggered the change
  // (otherwise clicking preset 2 for a template would get immediately
  // overridden by preset 1's fill). Tracked via a ref because we want
  // a one-shot guard, not part of effect dependencies.
  const skipNextAutoLoad = useRef(false);
  useEffect(() => {
    if (skipNextAutoLoad.current) {
      skipNextAutoLoad.current = false;
      return;
    }
    const matching = STARTER_PRESETS.find((p) => p.templateKey === templateKey);
    if (!matching) return;
    setFillText(JSON.stringify(matching.fill, null, 2));
    setResult(null);
    setParseError(null);
    // matching.format may be undefined; only override when set.
    if (matching.format) setFormat(matching.format);
  }, [templateKey]);

  function loadPreset(preset: StarterPreset) {
    skipNextAutoLoad.current = true;
    setTemplateKey(preset.templateKey);
    setFillText(JSON.stringify(preset.fill, null, 2));
    setFormat(preset.format ?? 'feed_1x1');
    setResult(null);
    setParseError(null);
  }

  async function onRender() {
    setPending(true);
    setResult(null);
    setParseError(null);
    let fill: unknown;
    try {
      fill = JSON.parse(fillText);
    } catch (err) {
      setParseError(founderErrorCopy(err, 'invalid JSON'));
      setPending(false);
      return;
    }
    try {
      const res = await devTemplateRender({
        templateKey,
        templateFill: fill,
        format,
        ...(selectedBrandId ? { brandId: selectedBrandId } : {}),
      });
      setResult(res);
    } catch (err) {
      setResult({
        ok: false,
        error: 'network_error',
        detail: founderErrorCopy(err, String(err)),
      });
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="min-h-dvh bg-[var(--paper)]">
      <TopNav active={null} />
      <main className="mx-auto max-w-[1280px] px-4 py-8 sm:px-6 sm:py-10">
        <header className="mb-7">
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
            ↘ dev · templates
          </p>
          <h1 className="mt-1 font-display text-[28px] font-bold leading-tight tracking-[-0.02em] text-[var(--ink)]">
            template render sandbox.
          </h1>
          <p className="mt-2 text-[13px] text-[var(--ink)]/65">
            POST a hand-authored fill to{' '}
            <code className="font-mono text-[12px]">/api/__dev/templates/render</code>. skips the
            LLM fill agent, hits the real Playwright pipeline. dev-only — the endpoint 404s in prod.
          </p>
        </header>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <section className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6">
            <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
              ↘ input
            </p>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
                  brand
                </span>
                <select
                  className="mt-1 w-full rounded-md border-[1.5px] border-[var(--ink)]/30 bg-white px-3 py-2 font-mono text-[12px] text-[var(--ink)] focus:border-[var(--ink)] focus:outline-none"
                  value={selectedBrandId}
                  onChange={(e) => setSelectedBrandId(e.target.value)}
                  disabled={brands.isLoading}
                >
                  <option value="">(default brand)</option>
                  {(brands.data ?? []).map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="block font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
                  format
                </span>
                <select
                  className="mt-1 w-full rounded-md border-[1.5px] border-[var(--ink)]/30 bg-white px-3 py-2 font-mono text-[12px] text-[var(--ink)] focus:border-[var(--ink)] focus:outline-none"
                  value={format}
                  onChange={(e) => setFormat(e.target.value)}
                >
                  {(selectedMeta?.formats ?? ['feed_1x1']).map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="mt-3 block">
              <span className="block font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
                template
              </span>
              <select
                className="mt-1 w-full rounded-md border-[1.5px] border-[var(--ink)]/30 bg-white px-3 py-2 font-mono text-[12px] text-[var(--ink)] focus:border-[var(--ink)] focus:outline-none"
                value={templateKey}
                onChange={(e) => setTemplateKey(e.target.value)}
                disabled={templates.isLoading}
              >
                {(templates.data ?? []).map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.label} ({t.key}){t.deprecated ? ' · deprecated' : ''}
                  </option>
                ))}
              </select>
              {selectedMeta ? (
                <p className="mt-1 font-mono text-[10.5px] text-[var(--ink)]/55">
                  {selectedMeta.description}
                </p>
              ) : null}
            </label>

            <div className="mt-4">
              <span className="block font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
                starter presets
              </span>
              <div className="mt-1 flex flex-wrap gap-2">
                {STARTER_PRESETS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => loadPreset(p)}
                    className="rounded-full border-[1.5px] border-[var(--ink)]/30 bg-white px-3 py-1 font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/70 transition-colors hover:border-[var(--ink)] hover:text-[var(--ink)]"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <label className="mt-4 block">
              <span className="block font-mono text-[10.5px] uppercase tracking-[0.12em] text-[var(--ink)]/55">
                template fill (JSON)
              </span>
              <textarea
                className="mt-1 h-[420px] w-full rounded-md border-[1.5px] border-[var(--ink)]/30 bg-white px-3 py-2 font-mono text-[11.5px] leading-snug text-[var(--ink)] focus:border-[var(--ink)] focus:outline-none"
                value={fillText}
                onChange={(e) => setFillText(e.target.value)}
                spellCheck={false}
              />
            </label>

            {parseError ? (
              <p className="mt-2 font-mono text-[11px] text-[var(--pulse)]">JSON: {parseError}</p>
            ) : null}

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={onRender}
                disabled={pending}
                className="rounded-full bg-[var(--ink)] px-5 py-2 font-display text-[14px] font-bold text-[var(--paper)] shadow-[3px_3px_0_var(--pulse)] transition-transform hover:translate-x-[-1px] hover:translate-y-[-1px] disabled:opacity-50"
              >
                {pending ? 'rendering…' : 'render'}
              </button>
            </div>
          </section>

          <section className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6">
            <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
              ↘ output
            </p>
            <TemplateResultPanel
              result={result}
              pending={pending}
              templates={templates.data ?? []}
            />
          </section>
        </div>
      </main>
    </div>
  );
}

function TemplateResultPanel({
  result,
  pending,
  templates,
}: {
  result: DevTemplateRenderResult | DevTemplateRenderError | null;
  pending: boolean;
  templates: DevTemplateListItem[];
}) {
  if (pending) {
    return (
      <p className="mt-3 font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
        rendering… (static: ~2-5s · video: 30s+)
      </p>
    );
  }
  if (!result) {
    return (
      <p className="mt-3 text-[13px] text-[var(--ink)]/55">
        render to see the output here. {templates.length} templates available in the dropdown.
      </p>
    );
  }
  if (!result.ok) {
    return (
      <div className="mt-3">
        <p className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--pulse)]">
          ↘ {result.error}
        </p>
        {result.detail ? (
          <p className="mt-2 text-[13px] text-[var(--ink)]/75">{result.detail}</p>
        ) : null}
        {result.issues && result.issues.length > 0 ? (
          <ul className="mt-3 list-disc pl-5 font-mono text-[11.5px] text-[var(--ink)]/75">
            {result.issues.map((iss, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: validation issues can share a path; index disambiguates the otherwise-colliding key
              <li key={`${iss.path}-${i}`}>
                <code className="text-[var(--pulse)]">{iss.path || '(root)'}</code>: {iss.message}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }
  // Video results return an mp4 URL; render in a <video> tag.
  const isVideo = result.url.endsWith('.mp4');
  return (
    <div className="mt-3 flex flex-col gap-3">
      {isVideo ? (
        <video
          src={result.url}
          controls
          autoPlay
          loop
          muted
          playsInline
          className="w-full rounded-md border-[1.5px] border-[var(--ink)]/30 bg-[var(--paper)]"
        />
      ) : (
        <img
          src={result.url}
          alt="template render"
          className="w-full rounded-md border-[1.5px] border-[var(--ink)]/30 bg-[var(--paper)]"
        />
      )}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-[11.5px] text-[var(--ink)]/75">
        <dt className="text-[var(--ink)]/50">brand</dt>
        <dd>{result.brand.name}</dd>
        <dt className="text-[var(--ink)]/50">template</dt>
        <dd>{result.templateKey}</dd>
        <dt className="text-[var(--ink)]/50">format</dt>
        <dd>
          {result.format} ({result.width}×{result.height})
        </dd>
        <dt className="text-[var(--ink)]/50">render time</dt>
        <dd>{result.durationMs}ms</dd>
        <dt className="text-[var(--ink)]/50">creative asset id</dt>
        <dd className="truncate" title={result.creativeAssetId}>
          {result.creativeAssetId.slice(0, 8)}…
        </dd>
      </dl>
    </div>
  );
}
